import { useState, useEffect } from "react";
import {
  Trash2, ChevronUp, ChevronDown, ChevronRight, ArrowRightLeft,
  Plane, Bus, Car, Sparkles, MapPin, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { SERVICE_LEVELS, TRANSPORT_OPTIONS, typeMeta } from "../../lib/adventureConstants";
import { COMMISSION_RATE } from "../../lib/adventureApi";
import { isTradeUser } from "../../lib/auth";
import { buildTimeline } from "../../lib/pricing";
import { fetchWeather, weatherIcon } from "../../lib/weather";
import { useMarket } from "../../lib/market";

const TICON = { flight: Plane, private: Car, coach: Bus };

export default function ItineraryPanel({
  items, modulesById, locationsById, serviceLevel, setServiceLevel, trip,
  pricing, onRemove, onMove, onAddTransfer, onOpenDetail, onGenerate, saving, commissionRate = COMMISSION_RATE,
}) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [transferGap, setTransferGap] = useState(null); // {index, from, to}
  const [weatherMap, setWeatherMap] = useState({});

  // fetch live weather for each unique destination in the itinerary
  useEffect(() => {
    const locs = {};
    items.forEach((it) => {
      if (it.kind === "transport") return;
      const m = modulesById[it.module_id];
      const loc = m && locationsById[m.location_id];
      if (loc) locs[loc.id] = loc;
    });
    Object.values(locs).forEach((loc) => {
      if (weatherMap[loc.id] !== undefined) return;
      fetchWeather(loc.lat, loc.lng).then((w) => {
        setWeatherMap((prev) => ({ ...prev, [loc.id]: w }));
      });
    });
    // eslint-disable-next-line
  }, [items, modulesById, locationsById]);

  const timeline = buildTimeline(items.map((it, i) => ({ ...it, uid: it.uid || i })), modulesById, locationsById);

  const gapAfter = (i) => {
    const a = items[i], b = items[i + 1];
    if (!a || !b) return null;
    if (a.kind === "transport" || b.kind === "transport") return null;
    const la = modulesById[a.module_id]?.location_id;
    const lb = modulesById[b.module_id]?.location_id;
    if (!la || !lb || la === lb) return null;
    return { from: locationsById[la]?.name, to: locationsById[lb]?.name };
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="itinerary-panel">
      {/* Header + service level */}
      <div className="p-5 border-b border-sand-dark">
        <h2 className="font-display font-bold text-lg tracking-tight">Client itinerary</h2>
        <p className="font-mono text-xs text-ink-muted uppercase tracking-widest mt-0.5">
          {pricing.duration} days · {items.filter((i) => i.kind !== "transport").length} experiences
        </p>
        <div className="flex mt-3 border border-sand-dark rounded-sm overflow-hidden" data-testid="service-level-tabs">
          {SERVICE_LEVELS.map((s) => (
            <button
              key={s.id} data-testid={`service-${s.id}`}
              onClick={() => setServiceLevel(s.id)}
              className={`flex-1 py-2 text-xs font-semibold tracking-wide transition-colors ${
                serviceLevel === s.id ? "bg-terracotta text-white" : "bg-white text-ink-secondary hover:bg-sand"
              }`}
            >{s.name}</button>
          ))}
        </div>
        <p className="text-[11px] text-ink-muted mt-2 leading-snug">
          {SERVICE_LEVELS.find((s) => s.id === serviceLevel)?.blurb}
        </p>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto thin-scroll p-4">
        {items.length === 0 && (
          <div className="text-center py-16 px-6" data-testid="empty-itinerary">
            <MapPin className="w-8 h-8 mx-auto text-sand-dark" />
            <p className="mt-3 font-display font-bold text-ink-secondary">No experiences yet</p>
            <p className="text-sm text-ink-muted mt-1">Drag experiences onto the map or hit <span className="text-terracotta font-semibold">Add</span> to start your client's trip.</p>
          </div>
        )}

        {timeline.map((row, i) => {
          const isTransport = row.type === "transport";
          const meta = typeMeta(row.type);
          const gap = gapAfter(i);
          return (
            <div key={row.key}>
              <div
                data-testid={`itinerary-item-${i}`}
                className="relative flex gap-3 group"
              >
                {/* day rail */}
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-mono text-xs font-bold text-white shrink-0"
                    style={{ background: isTransport ? "#3A6FB0" : meta.color }}>
                    {isTransport ? <ArrowRightLeft className="w-4 h-4" /> : row.dayRange}
                  </div>
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-sand-dark my-1" />}
                </div>

                {/* content */}
                <div className="flex-1 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <button
                        onClick={() => row.module && onOpenDetail(row.module)}
                        className="text-left font-display font-bold text-sm tracking-tight leading-snug hover:text-terracotta transition-colors">
                        {row.title}
                      </button>
                      <p className="text-xs text-ink-muted">
                        {isTransport ? "Transfer" : row.location}{!isTransport && ` · Day ${row.dayRange}`}
                      </p>
                      {!isTransport && row.module && (() => {
                        const w = weatherMap[row.module.location_id];
                        if (!w) return null;
                        const WI = weatherIcon(w.code, w.is_day);
                        return (
                          <span data-testid={`stop-weather-${i}`} className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-moss-light text-moss font-mono text-[10px] font-semibold">
                            <WI className="w-3 h-3" />{Math.round(w.temperature)}°C · {w.condition}
                          </span>
                        );
                      })()}
                    </div>
                    <span className="font-mono text-xs font-semibold whitespace-nowrap">
                      ${isTransport ? row.item.price : (row.module?.prices[row.item.service_override || serviceLevel])}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`item-up-${i}`} onClick={() => onMove(i, -1)} disabled={i === 0}
                      className="p-1 rounded-sm hover:bg-sand disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button data-testid={`item-down-${i}`} onClick={() => onMove(i, 1)} disabled={i === items.length - 1}
                      className="p-1 rounded-sm hover:bg-sand disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button data-testid={`item-remove-${i}`} onClick={() => onRemove(i)}
                      className="p-1 rounded-sm hover:bg-terracotta-light text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>

              {/* transfer gap */}
              {gap && (
                <div className="ml-12 -mt-2 mb-4" data-testid={`transfer-gap-${i}`}>
                  <button
                    onClick={() => setTransferGap({ index: i, ...gap })}
                    className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-sm border border-dashed border-[#3A6FB0]/50 bg-[#3A6FB0]/5 text-[#3A6FB0] hover:bg-[#3A6FB0]/10 transition-colors">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-medium">Transfer needed: {gap.from} → {gap.to}. Add one?</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pricing footer */}
      <div className="border-t border-sand-dark bg-[#FAFAFA] p-5 sticky bottom-0">
        <button onClick={() => setShowBreakdown((v) => !v)} data-testid="toggle-breakdown"
          className="flex items-center justify-between w-full mb-3">
          <span className="font-mono text-xs uppercase tracking-widest text-moss">Price breakdown</span>
          <ChevronRight className={`w-4 h-4 transition-transform ${showBreakdown ? "rotate-90" : ""}`} />
        </button>
        {showBreakdown && (
          <div className="space-y-1.5 mb-4 fade-up">
            {Object.entries(pricing.breakdown).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs text-ink-secondary">
                <span>{k}</span><span className="font-mono">${v}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs text-ink-muted pt-1 border-t border-sand-dark">
              <span>× {trip.travellers} travellers</span><span className="font-mono">group</span>
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-1">
          <span className="text-sm text-ink-secondary">Per person</span>
          <span className="font-mono font-semibold">${pricing.perPerson.toLocaleString()}</span>
        </div>
        <div className="flex items-end justify-between mb-1">
          <span className="text-sm font-semibold">Total journey</span>
          <span className="font-mono font-extrabold text-xl text-terracotta">${pricing.total.toLocaleString()}</span>
        </div>
        <div className="flex items-end justify-between mb-4">
          <span className="text-xs text-ink-muted">Deposit (20%)</span>
          <span className="font-mono text-xs text-ink-muted">${pricing.deposit.toLocaleString()}</span>
        </div>

        {isTradeUser() && pricing.total > 0 && (
          <div className="flex items-end justify-between mb-4 -mt-2 px-3 py-2 rounded-sm bg-moss-light" data-testid="commission-line">
            <span className="text-xs font-semibold text-moss">Your commission ({Math.round(commissionRate * 100)}%)</span>
            <span className="font-mono text-sm font-extrabold text-moss">${Math.round(pricing.total * commissionRate).toLocaleString()}</span>
          </div>
        )}

        <button
          data-testid="generate-plan-btn"
          onClick={onGenerate}
          disabled={items.length === 0 || saving}
          className="w-full flex items-center justify-center gap-2 bg-moss hover:bg-moss-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-40"
        >
          <Sparkles className="w-4 h-4" /> {saving ? "Creating…" : "Create My Adventure Plan"}
        </button>
      </div>

      {/* Transfer dialog */}
      <Dialog open={!!transferGap} onOpenChange={(o) => !o && setTransferGap(null)}>
        <DialogContent className="max-w-md rounded-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">
              {transferGap?.from} → {transferGap?.to}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-secondary -mt-2 mb-2">Choose how you'd like to travel between these stops.</p>
          <div className="space-y-2">
            {TRANSPORT_OPTIONS.map((opt) => {
              const Icon = TICON[opt.mode];
              return (
                <button
                  key={opt.mode} data-testid={`transport-${opt.mode}`}
                  onClick={() => { onAddTransfer(transferGap.index, transferGap, opt); setTransferGap(null); }}
                  className="w-full flex items-center gap-3 p-3 rounded-sm border border-sand-dark hover:border-terracotta hover:bg-terracotta-light/40 transition-colors text-left">
                  <div className="w-10 h-10 rounded-sm bg-[#3A6FB0]/10 flex items-center justify-center text-[#3A6FB0]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{opt.label}</p>
                    <p className="text-xs text-ink-muted">{opt.time} · {opt.note}</p>
                  </div>
                  <span className="font-mono font-semibold text-sm">${opt.price}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
